import { useEffect, useId, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import { uk } from "date-fns/locale";
import { AlertTriangle, CheckCircle2, FileText, Loader2, Undo2, X } from "lucide-react";
import { cn } from "@/lib/cn";
import { useDrawerA11y } from "@/hooks/useDrawerA11y";
import { reviewMeetingReport, type ControlItem } from "@/api/meetingsControl";
import { ApiClientError } from "@/api/client";

/**
 * D-055 S5 — Supervisor read-only перегляд звіту + review-дії (екран 5).
 *
 * Доктрина: керівник НЕ редагує debrief менеджера (read-only); може
 * прийняти (accept) або повернути на уточнення (return, коментар REQUIRED —
 * server-enforced S1). Return → D-043 needs_clarification → менеджер
 * resubmit-ить через канонічний wizard → pending_review (S3-цикл).
 * Жодних нових форм — це НЕ report-форма.
 */

const OUTCOME_LABELS: Record<string, string> = {
  agreed: "Домовились",
  followup: "Follow-up",
  interested: "Зацікавлений",
  notready: "Не готовий",
  complaint: "Скарга",
  contract: "Питання договору",
  payment: "Оплата / борг",
  escalate: "Залучити керівника",
  // legacy-половина outcome(16) — раптом старі звіти
  successful: "Успішно",
  no_answer: "Без відповіді",
  call_back_later: "Передзвонити пізніше",
  not_interested: "Не зацікавлений",
  follow_up_needed: "Потрібен follow-up",
  meeting_scheduled: "Зустріч заплановано",
  decision_pending: "Рішення очікується",
  other: "Інше",
};

const RISK_MARKER_LABELS: Record<string, string> = {
  competitor: "Конкурент",
  client_refusal: "Ризик відмови",
  lost_risk: "Ризик втрати клієнта",
};

export function ReviewReportDrawer({
  item,
  onClose,
  onDone,
}: {
  item: ControlItem | null;
  onClose: () => void;
  /** Після успішного accept/return — invalidate черги. */
  onDone: () => void;
}) {
  const open = item !== null;
  const panelRef = useDrawerA11y(open, onClose);
  const headingId = useId();

  const [mode, setMode] = useState<"view" | "return">("view");
  const [comment, setComment] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setMode("view");
      setComment("");
      setError(null);
    }
  }, [open]);

  const mutation = useMutation({
    mutationFn: ({ action, c }: { action: "accept" | "return"; c?: string }) =>
      reviewMeetingReport(item!.id, action, c),
    onSuccess: () => {
      onDone();
      onClose();
    },
    onError: (e) => {
      setError(
        e instanceof ApiClientError
          ? e.message || "Не вдалося виконати дію"
          : "Не вдалося виконати дію",
      );
    },
  });

  if (!item) return null;

  const fmt = (v: string | null, pattern = "dd.MM HH:mm") => {
    if (!v) return null;
    try {
      return format(parseISO(v.replace(" ", "T")), pattern, { locale: uk });
    } catch {
      return null;
    }
  };

  const r = item.report;
  const canReview = item.allowedActions.canReview;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose} aria-hidden>
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        className="relative flex h-full w-full flex-col bg-background shadow-2xl outline-none sm:w-[480px]"
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-background px-5 py-4">
          <h3 id={headingId} className="flex items-center gap-2 text-base font-semibold">
            <FileText className="h-4 w-4 text-primary" /> Звіт менеджера
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Закрити"
            className="rounded-md p-1 text-muted-foreground hover:text-foreground"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
          {/* Контекст зустрічі */}
          <div className="rounded-md border bg-muted/40 px-3 py-2.5">
            <div className="flex items-center gap-2 text-sm font-medium">
              <span className="truncate">{item.summary}</span>
              {item.risk && (
                <span className="inline-flex items-center gap-1 rounded-full bg-destructive/10 px-2 py-0.5 text-[10.5px] font-bold text-destructive">
                  <AlertTriangle className="h-3 w-3" aria-hidden /> risk-результат
                </span>
              )}
            </div>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {[
                item.managerName ? `Менеджер: ${item.managerName}` : null,
                item.accountName,
                item.occurredAt ? `проведено ${fmt(item.occurredAt)}` : null,
                item.reportSubmittedAt ? `звіт подано ${fmt(item.reportSubmittedAt)}` : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            </p>
            {item.reportSubmissionMode === "supervisor_on_behalf" && (
              <p className="mt-1.5 rounded-md bg-violet px-2.5 py-1.5 text-[11.5px] text-violet-foreground">
                <b>Заповнив керівник{item.reportSubmittedByName ? ` ${item.reportSubmittedByName}` : ""}</b>
                {item.reportSubmittedForName ? ` від імені ${item.reportSubmittedForName}` : ""}
                {item.reportOnBehalfReason ? ` · Причина: ${item.reportOnBehalfReason}` : ""}
                {" · review не застосовується"}
              </p>
            )}
          </div>

          {r ? (
            <>
              {/* Результат + ризик-маркери */}
              <div className="flex flex-wrap items-center gap-1.5">
                {r.outcome && (
                  <span
                    className={cn(
                      "rounded-full px-2.5 py-1 text-xs font-semibold",
                      item.risk
                        ? "bg-destructive/10 text-destructive"
                        : "bg-primary/10 text-primary",
                    )}
                  >
                    {OUTCOME_LABELS[r.outcome] ?? r.outcome}
                  </span>
                )}
                {r.riskMarkers.map((m) => (
                  <span
                    key={m}
                    className="rounded-full bg-destructive/10 px-2.5 py-1 text-xs font-semibold text-destructive"
                  >
                    {RISK_MARKER_LABELS[m] ?? m}
                  </span>
                ))}
              </div>

              {/* Debrief — READ-ONLY (доктрина: керівник не переписує).
                  D-151 S2 (C8): on-behalf → чесний label + атрибуція. */}
              <div>
                <p className="mb-1 text-xs font-semibold text-muted-foreground">
                  {item.reportSubmissionMode === "supervisor_on_behalf"
                    ? "Підсумок керівника за менеджера (read-only)"
                    : "Підсумок менеджера (read-only)"}
                </p>
                <div className="whitespace-pre-wrap rounded-md border bg-background px-3 py-2.5 text-sm">
                  {r.text || "—"}
                </div>
              </div>

              {/* Наступна дія */}
              <div className="rounded-md border px-3 py-2.5 text-xs">
                <span className="font-semibold">Наступна дія: </span>
                {r.nextActionRequired === "yes" ? (
                  "створено дію зі звіту"
                ) : r.nextActionReason ? (
                  <>без дій · причина: {r.nextActionReason}</>
                ) : (
                  <span className="font-semibold text-destructive">
                    без дій і без причини — потребує уваги
                  </span>
                )}
              </div>

              {/* Попередній коментар повернення */}
              {item.reviewStatus === "returned" && item.reviewComment && (
                <div className="rounded-md border border-warning-foreground/20 bg-warning px-3 py-2.5 text-xs text-warning-foreground">
                  <span className="font-semibold">Повернуто на уточнення: </span>
                  {item.reviewComment}
                </div>
              )}
            </>
          ) : (
            <div className="rounded-md border border-warning-foreground/20 bg-warning px-3 py-3 text-sm text-warning-foreground">
              Звіт ще не подано.
              {item.reportSlaDueAt && !item.reportOverdueSince && (
                <> SLA: до {fmt(item.reportSlaDueAt)}.</>
              )}
              {item.reportOverdueSince && (
                <> Прострочено з {fmt(item.reportOverdueSince)} — менеджер сповіщений (D-043).</>
              )}
            </div>
          )}

          {/* Return-форма (comment REQUIRED) */}
          {mode === "return" && (
            <div>
              <p className="mb-1 text-xs font-semibold text-muted-foreground">
                Коментар для менеджера <span className="text-destructive">*</span>
              </p>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={3}
                autoFocus
                placeholder="Що саме уточнити чи доповнити у звіті?"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
              <p className="mt-1 text-[11px] text-muted-foreground">
                Менеджер отримає сповіщення (D-043) і подасть звіт повторно через канонічний wizard.
              </p>
            </div>
          )}

          {error && (
            <p className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive">
              {error}
            </p>
          )}
        </div>

        {/* Дії */}
        <div className="border-t px-5 py-3.5">
          {canReview ? (
            mode === "view" ? (
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => mutation.mutate({ action: "accept" })}
                  disabled={mutation.isPending}
                  className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-md bg-primary px-3 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                >
                  {mutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <CheckCircle2 className="h-4 w-4" />
                  )}
                  Прийняти
                </button>
                <button
                  type="button"
                  onClick={() => setMode("return")}
                  disabled={mutation.isPending}
                  className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-md border border-border px-3 py-2.5 text-sm font-semibold hover:bg-muted disabled:opacity-50"
                >
                  <Undo2 className="h-4 w-4" /> Повернути на уточнення
                </button>
              </div>
            ) : (
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => mutation.mutate({ action: "return", c: comment.trim() })}
                  disabled={mutation.isPending || comment.trim() === ""}
                  className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-md bg-destructive px-3 py-2.5 text-sm font-semibold text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50"
                >
                  {mutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Undo2 className="h-4 w-4" />
                  )}
                  Повернути на уточнення
                </button>
                <button
                  type="button"
                  onClick={() => setMode("view")}
                  disabled={mutation.isPending}
                  className="rounded-md border border-border px-3 py-2.5 text-sm font-semibold hover:bg-muted"
                >
                  Назад
                </button>
              </div>
            )
          ) : (
            <p className="text-center text-xs text-muted-foreground">
              {item.reportSubmitted
                ? item.reviewStatus === "accepted"
                  ? "Звіт прийнято."
                  : item.reviewStatus === "returned"
                    ? "Повернуто на уточнення — очікує resubmit менеджера."
                    : "Review недоступний (власний звіт або стан не дозволяє)."
                : "Review доступний після подання звіту."}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
